import { useState, useRef, useMemo, useEffect } from 'react';

export default function useOrderedList(items, { storageKey, userId, idKey = 'patientId', nameKey = 'patientName' }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [storedOrder, setStoredOrder] = useState(null);
    const dragFrom = useRef(null);
    const dragTo = useRef(null);

    useEffect(() => {
        if (!userId) return;
        try {
            const raw = localStorage.getItem(`${storageKey}:${userId}`);
            if (raw) setStoredOrder(JSON.parse(raw));
        } catch {}
    }, [userId, storageKey]);

    const orderedItems = useMemo(() => {
        if (!storedOrder || !items.length) return items;
        const orderMap = new Map(storedOrder.map((id, i) => [id, i]));
        return [...items].sort((a, b) => {
            const ia = orderMap.has(a[idKey]) ? orderMap.get(a[idKey]) : Infinity;
            const ib = orderMap.has(b[idKey]) ? orderMap.get(b[idKey]) : Infinity;
            return ia - ib;
        });
    }, [items, storedOrder, idKey]);

    const displayedItems = useMemo(() => {
        if (!searchQuery.trim()) return orderedItems;
        const q = searchQuery.toLowerCase();
        return orderedItems.filter(item =>
            (item[nameKey] || '').toLowerCase().includes(q)
        );
    }, [orderedItems, searchQuery, nameKey]);

    const canDrag = !searchQuery.trim();

    function handleDragEnd() {
        const from = dragFrom.current;
        const to = dragTo.current;
        dragFrom.current = null;
        dragTo.current = null;
        if (from === null || to === null || from === to) return;

        const reordered = [...orderedItems];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);

        const newOrder = reordered.map(item => item[idKey]);
        setStoredOrder(newOrder);
        if (userId) {
            localStorage.setItem(`${storageKey}:${userId}`, JSON.stringify(newOrder));
        }
    }

    function dragProps(index) {
        return {
            draggable: canDrag,
            onDragStart: e => { dragFrom.current = index; e.dataTransfer.effectAllowed = 'move'; },
            onDragEnter: () => { dragTo.current = index; },
            onDragOver: e => e.preventDefault(),
            onDragEnd: handleDragEnd,
        };
    }

    return { searchQuery, setSearchQuery, displayedItems, canDrag, dragProps };
}
